import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { useHudStore } from "../../store/hudStore";
import { computeView } from "../../store/selectors";
import { Button, Progress } from "../../design-system/components";
import { bloom } from "../ui";
import "./overlays.css";

const panel: CSSProperties = {
  position: "relative", width: "100%", display: "flex", flexDirection: "column",
  alignItems: "center", gap: 16,
  borderRadius: "var(--r-lg)", border: "1px solid var(--line2)",
  background: "var(--card)", animation: "pop .35s var(--ease)", textAlign: "center",
};
const caption: CSSProperties = {
  position: "relative", fontSize: 14, fontWeight: 600, color: "var(--accent-ink)",
};
const description: CSSProperties = {
  position: "relative", fontSize: 14, fontWeight: 400, lineHeight: 1.5,
  color: "var(--sub)", maxWidth: "34ch", textWrap: "pretty",
};

// Native modal dialogs keep the background inert, contain keyboard focus and
// provide the browser's Escape behavior. Only the highest-priority overlay is
// shown; pending achievements appear in turn without changing their game state.
function Modal({ labelledBy, describedBy, onDismiss, dismissOnBackdrop = false, children }: {
  labelledBy: string; describedBy: string; onDismiss: () => void;
  dismissOnBackdrop?: boolean; children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!dialog.open) dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="ds-dialog hud-achievement-dialog"
      data-hud-dialog
      aria-modal="true"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onCancel={(e) => { e.preventDefault(); onDismiss(); }}
      onClick={(e) => { if (dismissOnBackdrop && e.target === e.currentTarget) onDismiss(); }}
    >
      {children}
    </dialog>
  );
}

export default function Overlays() {
  const st = useHudStore();
  const v = computeView(st);
  return (
    <>
      {v.levelUp ? (
        <Modal key="level" labelledBy="level-up-title" describedBy="level-up-note" onDismiss={st.closeLevelUp}>
          <div className="hud-achievement-panel" style={panel}>
            {v.confetti.map((c, i) => (
              <i key={i} aria-hidden="true" style={{ position: "absolute", top: 0, left: c.left, width: 8, height: 12, borderRadius: 2, pointerEvents: "none", background: c.color, animation: `confetti ${c.dur} var(--ease) ${c.delay} forwards`, "--dx": c.dx } as CSSProperties} />
            ))}
            <h2 id="level-up-title" style={caption}>Level up</h2>
            <div style={{ position: "relative", fontFamily: "var(--display)", fontSize: "clamp(56px,10vw,80px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1, color: "var(--accent-bright)" }}>{v.level}</div>
            <p style={{ position: "relative", fontSize: 16, fontWeight: 600, color: "var(--ink)" }}>Arlou · {v.title}</p>
            <p id="level-up-note" style={description}>{v.levelUpNote}</p>
            <Button variant="primary" autoFocus onClick={st.closeLevelUp}>Keep going</Button>
          </div>
        </Modal>
      ) : v.mainDoneOpen ? (
        <Modal key="main" labelledBy="main-done-title" describedBy="main-done-note" onDismiss={st.closeMainDone}>
          <div className="hud-achievement-panel" style={{ ...panel, borderColor: "var(--gold)" }}>
            <span aria-hidden="true" style={{ position: "relative", width: 64, height: 64, borderRadius: "var(--r-sm)", display: "grid", placeItems: "center", background: "var(--gold)", color: "var(--on-accent)" }}>
              <i className="fa-solid fa-trophy" style={{ fontSize: 28, lineHeight: 1 }} />
            </span>
            <p style={{ ...caption, color: "var(--orange-ink)" }}>Main quest complete</p>
            <h2 id="main-done-title" className="hud-achievement-title">{v.mainDoneName}</h2>
            <p id="main-done-note" style={description}>{v.mainDoneNote}</p>
            <Button variant="primary" autoFocus onClick={st.closeMainDone}>Put it on the shelf</Button>
          </div>
        </Modal>
      ) : v.badge ? (
        <Modal key="badge" labelledBy="badge-title" describedBy="badge-description" onDismiss={st.closeBadge} dismissOnBackdrop>
          <div className="hud-achievement-panel" style={panel}>
            <span aria-hidden="true" style={{ position: "relative", width: 64, height: 64, display: "grid", placeItems: "center" }}>
              <span style={{ position: "absolute", inset: 0, borderRadius: "var(--r-sm)", transform: "rotate(45deg)", background: v.badgeGem }} />
              <i className={v.badge.fa} style={{ position: "relative", fontSize: 24, lineHeight: 1, color: v.badgeIconColor }} />
            </span>
            <h2 id="badge-title" style={{ fontFamily: "var(--display)", fontSize: 24, fontWeight: 700, letterSpacing: "-0.01em", marginTop: 8, lineHeight: 1.2 }}>{v.badge.name}</h2>
            <p id="badge-description" style={description}>{v.badge.how}</p>
            <div style={{ width: "100%", marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, fontSize: 12, fontWeight: 600, color: "var(--mut)", marginBottom: 8 }}>
                <span>{v.badgeState}</span><span style={{ color: "var(--accent-ink)" }}>{v.badge.at}</span>
              </div>
              <Progress value={v.badge.pct} aria-label={`${v.badge.name} progress`} className="hud-achievement-progress" style={{ "--ds-tone": v.badgeGlow, "--achievement-glow": bloom(v.badgeGlow, v.badge.pct, 14) } as CSSProperties} />
            </div>
            <Button autoFocus onClick={st.closeBadge}>Close</Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
